import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { IconBolt, IconChevronRight, IconBrandStrava, IconWorld, IconCalendarEvent } from '@tabler/icons-react';
import { db, functions } from '../firebase';
import { getZwiftWorldForActivity } from '../utils/zwiftActivity';
import ActivityMap from './ActivityMap';

// Auto-upload event statuses that count as a real Zwift fix.
const COMPLETED_STATUSES = ['auto_uploaded', 'claim_completed'];
const ACTIVE_STATUSES = [...COMPLETED_STATUSES, 'auto_uploaded_pending', 'claim_pending'];
const EVENTS_FETCH_LIMIT = 100;
// How far back the onboarding looks for the rider's latest Zwift ride
// (~9 months), and a page bound so hyper-active accounts don't scan forever
// (6 pages x 200 = 1,200 activities).
const ZWIFT_LOOKBACK_DAYS = 274;
const ZWIFT_LOOKBACK_MAX_PAGES = 6;

const formatFixedDate = (ts) => {
  try {
    const d = ts?.toDate ? ts.toDate() : (typeof ts?.seconds === 'number' ? new Date(ts.seconds * 1000) : null);
    if (!d || Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) {
    return null;
  }
};

// The onboarding "aha": the moment a rider connects Strava, find their most recent
// Zwift ride, render the deterministic fixed map instantly (free, no credit, no write),
// and show a personalised before/after. One tap then enables auto-fix for every future
// indoor ride AND fixes this one for real on their Strava feed. For a rider who already
// has auto-fix on, it shows an "already set up" dashboard instead.
const ZwiftAhaCard = ({ user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const loadStartedRef = useRef(false);
  // The user's explicit artEnabled choice (boolean) or null when the doc has
  // none; handleCommit must not clobber an existing choice.
  const artExplicitRef = useRef(null);

  const [phase, setPhase] = useState('loading'); // loading | ready | no-zwift | error | already-setup
  const [activity, setActivity] = useState(null);
  const [worldName, setWorldName] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [dashboard, setDashboard] = useState(null);

  const [committing, setCommitting] = useState(false);
  const [committed, setCommitted] = useState(false);
  const [commitError, setCommitError] = useState('');

  useEffect(() => {
    if (!user || loadStartedRef.current) return;
    loadStartedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        // Already set up? Read the auto-upload settings first.
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        if (cancelled) return;
        const auto = userSnap.data()?.settings?.autoUpload || {};
        artExplicitRef.current = typeof auto.artEnabled === 'boolean' ? auto.artEnabled : null;

        // This page is about the INDOOR fix, so gate on the effective
        // indoor-fix state (explicit field wins; legacy docs fall back to the
        // old master toggle — mirrors the settings UI and backend). The legacy
        // `enabled` mirror alone is not enough: art-only users have
        // enabled=true with the indoor fix off, and must see the onboarding.
        const indoorFixOn = typeof auto.indoorFixEnabled === 'boolean' ?
          auto.indoorFixEnabled :
          auto.enabled === true;

        if (indoorFixOn) {
          // ===== Already set up: mini-dashboard =====
          const eventsSnap = await getDocs(query(
            collection(db, 'users', user.uid, 'autoUploadEvents'),
            orderBy('updatedAt', 'desc'),
            limit(EVENTS_FETCH_LIMIT)
          ));
          if (cancelled) return;

          const events = eventsSnap.docs.map((d) => d.data());
          const activeZwift = events.filter((ev) => ev.zwiftWorld && ACTIVE_STATUSES.includes(ev.status));
          const completedZwift = events.filter((ev) => ev.zwiftWorld && COMPLETED_STATUSES.includes(ev.status));
          const latest = activeZwift[0] || null;
          const worlds = new Set(activeZwift.map((ev) => ev.zwiftWorld));

          setDashboard({
            fixedCount: completedZwift.length,
            // ACTIVE is a superset of COMPLETED, so the difference is fixes
            // still being rendered/uploaded right now.
            inProgressCount: activeZwift.length - completedZwift.length,
            atLimit: eventsSnap.docs.length >= EVENTS_FETCH_LIMIT,
            worldsCount: worlds.size,
            lastFixedLabel: latest ? formatFixedDate(latest.updatedAt) : null,
          });
          setPhase('already-setup');
          posthog?.capture('fix_dashboard_viewed', { fixed_count: completedZwift.length });

          // Best-effort: render the most recent fixed ride for a warm proof-it-works visual.
          if (latest?.activityId) {
            try {
              const previewFix = httpsCallable(functions, 'previewZwiftFix');
              const preview = await previewFix({ activityId: String(latest.activityId), orientation: 'portrait' });
              if (!cancelled && preview?.data?.imageDataUrl) {
                setPreviewImage(preview.data.imageDataUrl);
                setWorldName(preview.data.world?.name || '');
              }
            } catch (previewErr) {
              console.warn('[ZwiftAhaCard] latest preview failed:', previewErr);
            }
          }
          return;
        }

        // ===== First-run onboarding =====
        // Scan back up to 9 months for the most recent Zwift ride. Strava
        // returns newest-first, so we page (200/page, bounded) until we find
        // one, run out of activities, or pass the cutoff.
        const getActivities = httpsCallable(functions, 'getStravaActivities');
        const cutoffMs = Date.now() - ZWIFT_LOOKBACK_DAYS * 24 * 60 * 60 * 1000;
        let zwiftActivity = null;
        let scannedCount = 0;
        for (let scanPage = 1; scanPage <= ZWIFT_LOOKBACK_MAX_PAGES; scanPage++) {
          const result = await getActivities({ perPage: 200, page: scanPage });
          if (cancelled) return;
          const activities = Array.isArray(result?.data) ? result.data : [];
          if (activities.length === 0) break;
          scannedCount += activities.length;

          zwiftActivity = activities.find((act) => (
            getZwiftWorldForActivity(act) && new Date(act.start_date).getTime() >= cutoffMs
          )) || null;
          if (zwiftActivity) break;

          const oldest = activities[activities.length - 1];
          const oldestMs = new Date(oldest?.start_date).getTime();
          if (activities.length < 200 || (Number.isFinite(oldestMs) && oldestMs < cutoffMs)) break;
        }

        if (!zwiftActivity) {
          setPhase('no-zwift');
          posthog?.capture('fix_aha_no_zwift', { activity_count: scannedCount });
          return;
        }

        setActivity(zwiftActivity);

        const previewFix = httpsCallable(functions, 'previewZwiftFix');
        const preview = await previewFix({ activityId: String(zwiftActivity.id), orientation: 'portrait' });
        if (cancelled) return;

        if (preview?.data?.isZwift && preview.data.imageDataUrl) {
          setPreviewImage(preview.data.imageDataUrl);
          setWorldName(preview.data.world?.name || '');
          setPhase('ready');
          posthog?.capture('fix_aha_rendered', {
            activity_id: String(zwiftActivity.id),
            world: preview.data.world?.slug || null,
          });
        } else {
          // Detected as Zwift client-side but the backend couldn't render (e.g. no route).
          setPhase('no-zwift');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[ZwiftAhaCard] init failed:', err);
        setErrorMessage(t('fixPage.ahaError', 'We could not load your last ride just now. You can still turn on auto-fix below.'));
        setPhase('error');
      }
    })();

    // Release the run-once guard on cleanup. Under StrictMode's dev
    // double-mount the first run is cancelled during its first await; without
    // this reset the re-run bails on the guard and the card spins forever.
    return () => {
      cancelled = true;
      loadStartedRef.current = false;
    };
  }, [user, posthog, t]);

  const handleCommit = useCallback(async () => {
    if (!user || committing || committed) return;

    setCommitting(true);
    setCommitError('');

    try {
      // 1) Turn on indoor-fix auto-upload. Art maps are untouched when the
      // user already made an explicit choice; only docs WITHOUT one get
      // artEnabled pinned to false, where the legacy fallback
      // (enabled && !zwiftOnly) would otherwise silently switch art on too.
      await updateDoc(doc(db, 'users', user.uid), {
        'settings.autoUpload.indoorFixEnabled': true,
        ...(artExplicitRef.current === null ? { 'settings.autoUpload.artEnabled': false } : {}),
        // Mirror the legacy master for any external analytics still reading it.
        'settings.autoUpload.enabled': true,
      });
      posthog?.capture('fix_auto_upload_enabled', { source: 'onboarding_aha' });

      // 2) Fix this specific ride now and upload it to Strava (user-initiated, no follower gate).
      if (activity?.id) {
        const fixNow = httpsCallable(functions, 'fixZwiftActivityNow');
        await fixNow({ activityId: String(activity.id) });
      }

      setCommitted(true);
      posthog?.capture('fix_committed', {
        activity_id: activity?.id ? String(activity.id) : null,
        world: worldName || null,
        had_preview: !!previewImage,
      });
    } catch (err) {
      console.error('[ZwiftAhaCard] commit failed:', err);
      setCommitError(t('fixPage.commitError', 'Something went wrong turning on auto-fix. Please try again.'));
    } finally {
      setCommitting(false);
    }
  }, [user, committing, committed, activity, worldName, previewImage, posthog, t]);

  if (committed) {
    return (
      <div className="zwift-aha-card zwift-aha-done">
        <div className="zwift-aha-done-check" aria-hidden="true">✓</div>
        <h2>{t('fixPage.doneTitle', 'Done. Your indoor rides are fixed.')}</h2>
        <p>
          {worldName
            ? t('fixPage.doneBodyWorld', { defaultValue: 'Your last ride now shows the real {{world}} map on Strava (it lands in about a minute). Every future Zwift ride will be fixed automatically.', world: worldName })
            : t('fixPage.doneBody', 'Your last ride is being fixed on Strava now, and every future Zwift ride will be fixed automatically.')}
        </p>
        <div className="zwift-aha-done-actions">
          <button type="button" className="challenge-cta-button" onClick={() => navigate('/settings#zwift-auto-upload')}>
            {t('fixPage.doneManage', 'Manage auto-fix settings')}
            <IconChevronRight size={18} />
          </button>
          <button type="button" className="zwift-aha-secondary-link" onClick={() => navigate('/art')}>
            {t('fixPage.doneExploreArt2', 'Want art on your outdoor rides? Explore Art')}
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'already-setup') {
    const d = dashboard || {};
    const countLabel = d.fixedCount > 0 ? `${d.fixedCount}${d.atLimit ? '+' : ''}` : '0';
    return (
      <div className="zwift-aha-card zwift-aha-dashboard">
        <div className="zwift-aha-header">
          <span className="zwift-aha-eyebrow">
            <IconBrandStrava size={16} />
            {t('fixPage.dashEyebrow', 'Auto-fix is on')}
          </span>
          <h2>{t('fixPage.dashTitle', "You're all set.")}</h2>
        </div>

        <div className="zwift-aha-stat-hero">
          <span className="zwift-aha-stat-number">{countLabel}</span>
          <span className="zwift-aha-stat-label">
            {d.fixedCount === 1
              ? t('fixPage.dashOneFixed', 'indoor ride fixed')
              : t('fixPage.dashManyFixed', 'indoor rides fixed')}
          </span>
          {d.inProgressCount > 0 && (
            <span className="zwift-aha-stat-inprogress">
              {d.inProgressCount === 1
                ? t('fixPage.dashOneInProgress', '…and 1 being fixed right now')
                : t('fixPage.dashManyInProgress', { defaultValue: '…and {{n}} being fixed right now', n: d.inProgressCount })}
            </span>
          )}
        </div>

        {previewImage ? (
          <div className="zwift-aha-latest">
            <div className="zwift-aha-latest-map">
              <img src={previewImage} alt={t('fixPage.dashLatestAlt', 'Your latest fixed ride')} />
            </div>
            <div className="zwift-aha-latest-meta">
              <span className="zwift-aha-latest-label">{t('fixPage.dashLatest', 'Latest fix')}</span>
              {worldName && <span className="zwift-aha-latest-world">{worldName}</span>}
              {d.lastFixedLabel && <span className="zwift-aha-latest-date">{d.lastFixedLabel}</span>}
            </div>
          </div>
        ) : (
          <p className="zwift-aha-dashboard-note">
            {t('fixPage.dashWaiting', 'We will fix your next indoor ride automatically and upload it to Strava.')}
          </p>
        )}

        {(d.worldsCount > 0 || d.lastFixedLabel) && (
          <div className="zwift-aha-ministats">
            {d.worldsCount > 0 && (
              <div className="zwift-aha-ministat">
                <IconWorld size={18} />
                <span>{t('fixPage.dashWorlds', { defaultValue: '{{n}} worlds fixed', n: d.worldsCount })}</span>
              </div>
            )}
            {d.lastFixedLabel && (
              <div className="zwift-aha-ministat">
                <IconCalendarEvent size={18} />
                <span>{t('fixPage.dashLast', { defaultValue: 'Last fixed {{date}}', date: d.lastFixedLabel })}</span>
              </div>
            )}
          </div>
        )}

        <div className="zwift-aha-done-actions">
          <button type="button" className="challenge-cta-button" onClick={() => navigate('/settings#zwift-auto-upload')}>
            {t('fixPage.doneManage', 'Manage auto-fix settings')}
            <IconChevronRight size={18} />
          </button>
          <button type="button" className="zwift-aha-secondary-link" onClick={() => navigate('/art')}>
            {t('fixPage.doneExploreArt2', 'Want art on your outdoor rides? Explore Art')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="zwift-aha-card">
      <div className="zwift-aha-header">
        <span className="zwift-aha-eyebrow">
          <IconBrandStrava size={16} />
          {t('fixPage.ahaEyebrow', 'Your latest indoor ride')}
        </span>
        <h2>
          {phase === 'ready' && worldName
            ? t('fixPage.ahaTitleWorld', { defaultValue: 'Here is your ride, fixed: the real {{world}} map.', world: worldName })
            : t('fixPage.ahaTitle', 'Let us fix your last indoor ride')}
        </h2>
      </div>

      {phase === 'loading' && (
        <div className="zwift-aha-loading">
          <div className="zwift-aha-spinner" />
          <p>{t('fixPage.ahaLoading', 'Finding your last Zwift ride and rebuilding the map…')}</p>
        </div>
      )}

      {phase === 'ready' && (
        <div className="zwift-aha-beforeafter">
          <div className="zwift-aha-panel">
            <span className="zwift-aha-panel-label zwift-aha-panel-label-before">
              {t('fixPage.beforeLabel', 'What Strava shows now')}
            </span>
            <div className="zwift-aha-map zwift-aha-map-before">
              <ActivityMap activity={activity} width={360} height={450} />
            </div>
            <span className="zwift-aha-panel-caption">
              {t('fixPage.beforeCaption', 'Your ride stranded in the ocean near the Solomon Islands.')}
            </span>
          </div>

          <div className="zwift-aha-arrow" aria-hidden="true">→</div>

          <div className="zwift-aha-panel">
            <span className="zwift-aha-panel-label zwift-aha-panel-label-after">
              {t('fixPage.afterLabel', 'Fixed by Mythic Maps')}
            </span>
            <div className="zwift-aha-map zwift-aha-map-after">
              <img src={previewImage} alt={t('fixPage.afterAlt', 'Your ride on the real Zwift world map')} />
            </div>
            <span className="zwift-aha-panel-caption">
              {worldName
                ? t('fixPage.afterCaptionWorld', { defaultValue: 'The actual {{world}} route, on your activity.', world: worldName })
                : t('fixPage.afterCaption', 'The actual Zwift world route, on your activity.')}
            </span>
          </div>
        </div>
      )}

      {phase === 'no-zwift' && (
        <div className="zwift-aha-empty">
          <p className="zwift-aha-empty-title">
            {t('fixPage.noZwiftTitle', 'No indoor ride to fix yet.')}
          </p>
          <p>
            {t('fixPage.noZwiftBody', 'Turn on auto-fix now, and the next time you ride Zwift (or another indoor app) we will swap the broken Solomon Islands map for the real world map automatically.')}
          </p>
        </div>
      )}

      {phase === 'error' && (
        <div className="zwift-aha-empty">
          <p>{errorMessage}</p>
        </div>
      )}

      {phase !== 'loading' && (
        <>
          <button
            type="button"
            className="zwift-aha-commit"
            onClick={handleCommit}
            disabled={committing}
          >
            <IconBolt size={20} />
            {committing
              ? t('fixPage.commitLoading', 'Turning on auto-fix…')
              : (activity
                ? t('fixPage.commitWithRide', 'Fix this ride + auto-fix every future indoor ride')
                : t('fixPage.commitNoRide', 'Auto-fix every indoor ride'))}
          </button>
          <p className="zwift-aha-reassure">
            {t('fixPage.commitReassure', 'Free. We only change the activity photo, never your route, time, or stats. Turn it off anytime in two clicks.')}
          </p>
          {commitError && <p className="zwift-aha-commit-error">{commitError}</p>}
        </>
      )}
    </div>
  );
};

export default ZwiftAhaCard;
